import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '@/api/services/NotificationService';
import { refreshAccessToken } from '@/auth/refreshManager';
import { tokenStore } from '@/auth/tokenStore';
import { config } from '@/config';
import { playBellSound } from '@/lib/notificationSound';
import { AGENCY_AGREEMENT_QUERY_KEY } from '@/hooks/useAgency';
import { ALL_DOCUMENTS_QUERY_KEY } from '@/hooks/useAllDocuments';
import { COMPANY_CARD_QUERY_KEY } from '@/hooks/useCompanyCard';
import { NOTIFICATIONS_KEY, upsertNotification } from '@/hooks/useNotifications';
import { PROFILE_QUERY_KEY } from '@/hooks/useProfile';
import { REQUIRED_DATA_QUERY_KEY } from '@/hooks/useRequiredData';
import { ROUTES } from '@/routes/paths';
import { useAuth } from '@/stores/authContext';
import type { NotificationItem, SseEnvelope } from '@/types/notifications';
import { type SystemActionKind, systemAction } from './notificationCatalog';
import { SseClient } from './sseClient';

const SSE_URL = `${config.apiBaseUrl}/notification/open-sse`;

/**
 * Глобальный real-time: один SSE-поток на сессию. Системные сообщения → инвалидация кэша
 * (авто-обновление сделки/профиля) и служебные действия; видимые → лента колокольчика.
 * Монтируется один раз в AppLayout (под Router/Query/Auth-провайдерами, после логина).
 */
export function useRealtime(): void {
  const { isAuthenticated, logout } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    function handleSystem(action: SystemActionKind, msg: SseEnvelope): void {
      switch (action) {
        case 'dealUpdate': {
          const bargainId = msg.payload?.bargainId;
          if (typeof bargainId === 'number') {
            // Префиксная инвалидация: накрывает steps/documents/consignee и пр. этой сделки.
            void queryClient.invalidateQueries({ queryKey: ['bargain', bargainId] });
          }
          void queryClient.invalidateQueries({ queryKey: ['bargains'] }); // список (статус мог смениться)
          break;
        }
        case 'userInfoUpdate': {
          void queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
          void queryClient.invalidateQueries({ queryKey: COMPANY_CARD_QUERY_KEY });
          void queryClient.invalidateQueries({ queryKey: REQUIRED_DATA_QUERY_KEY });
          void queryClient.invalidateQueries({ queryKey: ['bargain'] }); // реквизиты сторон в сделках
          break;
        }
        case 'accreditationUpdate': {
          // Анкета/агентский договор/документ аттестации подписаны или пересозданы → перечитать:
          // список документов аттестации (бейдж статуса), агентский договор (его вкладка — отдельный
          // запрос), обязательные данные (баннеры/гейт продажи) и профиль (приёмка анкеты может завершить
          // аттестацию — isVerified, от него зависит переключение онбординг→плитки на «Главной»).
          // События общие для анкеты и договора (различить по типу нельзя), поэтому инвалидируем всё
          // связанное разом — React Query перезапросит только активные/устаревшие запросы.
          void queryClient.invalidateQueries({ queryKey: ALL_DOCUMENTS_QUERY_KEY });
          void queryClient.invalidateQueries({ queryKey: AGENCY_AGREEMENT_QUERY_KEY });
          void queryClient.invalidateQueries({ queryKey: REQUIRED_DATA_QUERY_KEY });
          void queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
          break;
        }
        case 'logout': {
          logout();
          navigate(ROUTES.LOGIN, { replace: true });
          break;
        }
        case 'ignore':
          break;
      }
    }

    // Подтверждённые в этой сессии id — чтобы не слать повторный confirm на тот же id (догон при
    // реконнекте присылает те же события). Живёт в области эффекта: сбрасывается при входе/выходе.
    const confirmedIds = new Set<string>();

    function confirmDelivered(id: string): void {
      if (confirmedIds.has(id)) {
        return;
      }
      confirmedIds.add(id); // оптимистично — глушим дубли в полёте
      void notificationService.confirm(id).catch(() => {
        confirmedIds.delete(id); // при сбое разрешаем повтор в следующем догоне этой сессии
      });
    }

    function handleMessage(msg: SseEnvelope): void {
      const type = (msg.type ?? '').toLowerCase();
      if (!type) {
        return; // служебное без типа (напр. ping без payload-типа)
      }
      // Гасим SSE-догон: подтверждаем доставку любого сообщения с id — и системного, и видимого
      // (оба сохранены в БД с delivered=false). Fire-and-forget, не влияет на обработку ниже.
      if (msg.id) {
        confirmDelivered(msg.id);
      }
      const action = systemAction(type);
      if (action) {
        handleSystem(action, msg);
        return;
      }
      if (!msg.id) {
        return; // видимое без id невозможно дедуплицировать — пропускаем
      }
      const item: NotificationItem = {
        id: msg.id,
        type,
        // SSE-обёртка даты не несёт → ставим клиентское время (на следующем /list заменится серверным).
        createdDate: msg.payload?.createdDate ?? new Date().toISOString(),
        isRead: false,
        displayable: true,
        payload: msg.payload,
      };
      // Звук колокольчика — только на действительно новое уведомление (нет в кэше): дубли SSE-догона и
      // уже загруженную REST-историю не озвучиваем. Пачку (напр. догон при реконнекте) движок сводит к
      // одному сигналу (ограничение частоты) и уважает переключатель «звук уведомлений».
      const known = queryClient.getQueryData<NotificationItem[]>(NOTIFICATIONS_KEY);
      const isNew = !(known ?? []).some((n) => n.id === item.id);
      upsertNotification(queryClient, item);
      if (isNew) {
        playBellSound();
      }
    }

    const client = new SseClient({
      url: SSE_URL,
      getToken: () => tokenStore.getAccess(),
      onAuthError: () => refreshAccessToken(),
      onMessage: handleMessage,
    });
    client.start();
    return () => client.stop();
  }, [isAuthenticated, queryClient, navigate, logout]);
}
